import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DotsSixVertical, X } from '@phosphor-icons/react';
import { CrmField } from '../DraggableCrmField';
import { ContactRecord } from 'src/services/integration-app.service';
import { cn } from 'src/utils/className';
import { IconButton } from 'src/components/IconButton';

interface MappedFieldItemProps {
  field: CrmField;
  sampleContacts?: ContactRecord[];
  onRemove: () => void;
  disabled?: boolean;
}

export const MappedFieldItem: React.FC<MappedFieldItemProps> = ({
  field,
  sampleContacts = [],
  onRemove,
  disabled = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: field.value,
    data: { field },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 200ms ease',
  };

  const getSampleValues = (): string[] => {
    if (sampleContacts.length === 0) return [];

    return sampleContacts
      .slice(0, 3)
      .map((contact) => {
        const fieldValue =
          contact.fields[field.value as keyof typeof contact.fields];
        return fieldValue ? String(fieldValue) : '';
      })
      .filter((value) => value.trim() !== '');
  };

  const sampleValues = getSampleValues();
  const hasSampleValues = sampleValues.length > 0;

  if (isDragging) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="h-[52px] bg-neutral-200/50 border border-dashed border-primary/30 rounded-lg opacity-50"
      />
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        'group relative flex items-center gap-3 p-3 bg-neutral-0 border border-neutral-200 rounded-lg',
        'hover:border-primary-300 hover:shadow-sm transition-all',
        'cursor-grab active:cursor-grabbing',
      )}
    >
      <DotsSixVertical
        size={16}
        className="text-neutral-400 group-hover:text-neutral-600 transition-colors shrink-0"
      />
      <div className="flex flex-col min-w-0 flex-1">
        <span className="font-medium text-sm text-neutral-700 truncate">
          {field.label}
        </span>
        {hasSampleValues && (
          <span className="text-[10px] text-neutral-500 truncate mt-0.5">
            {sampleValues.join(', ')}
          </span>
        )}
      </div>
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        disabled={disabled}
        aria-label={`Remove ${field.label}`}
        className="opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <X size={14} />
      </IconButton>
    </div>
  );
};
